import { useState, useCallback, useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';
import { IconButton, Tooltip, type SxProps, type Theme } from '@mui/material';
import { Check as CheckIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { colors } from '@/theme/tokens';

interface CopyButtonProps {
  /** Text written to the clipboard on click. */
  text: string;
  /** Tooltip + accessible label while idle. Defaults to "Copy". */
  title?: string;
  /** Tint for dark surfaces (dark modal headers, code boxes). */
  dark?: boolean;
  /** Icon size in px. Defaults to 15. */
  size?: number;
  sx?: SxProps<Theme>;
}

/**
 * The single shared "copy to clipboard" affordance: an icon button that copies
 * `text` and confirms with a brief check-mark + "Copied" tooltip.
 */
export function CopyButton({ text, title = 'Copy', dark = false, size = 15, sx }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<number | undefined>(undefined);
  const mounted = useRef(true);

  // Clear any pending reset when the button unmounts, and stop a clipboard
  // write that resolves after unmount from touching state.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      window.clearTimeout(resetTimer.current);
    };
  }, []);

  const handleCopy = useCallback(
    (e: MouseEvent) => {
      // Keep row/card/breadcrumb click handlers from firing when the button
      // sits inside a clickable container.
      e.stopPropagation();
      // Confirm only once the write actually resolves. The Clipboard API can be
      // absent (insecure context) or reject; stay silent and never show a false
      // "Copied" in those cases.
      const write = navigator.clipboard?.writeText(text);
      if (!write) return;
      write
        .then(() => {
          if (!mounted.current) return;
          setCopied(true);
          // Restart the reset window so rapid re-clicks don't leave a stale
          // timer flipping the icon back early.
          window.clearTimeout(resetTimer.current);
          resetTimer.current = window.setTimeout(() => setCopied(false), 1400);
        })
        .catch(() => {});
    },
    [text],
  );

  const label = copied ? 'Copied' : title;
  return (
    <Tooltip title={label}>
      <IconButton
        size="small"
        onClick={handleCopy}
        aria-label={label}
        sx={{
          color: copied ? colors.success.dark : dark ? '#8B9DB5' : 'text.secondary',
          '&:hover': dark
            ? { bgcolor: 'rgba(255,255,255,.1)', color: '#fff' }
            : { color: colors.primary.main },
          ...sx,
        }}
      >
        {copied ? <CheckIcon sx={{ fontSize: size }} /> : <CopyIcon sx={{ fontSize: size }} />}
      </IconButton>
    </Tooltip>
  );
}

interface CopyDeviceIdProps {
  /** Canonical device id (machine_id / MQTT / analytics key) — copied when present. */
  machineId?: string | null;
  /** Identifier shown at the call site, copied only when no machine_id exists
   *  (unenrolled / legacy rows): serial number, asset number, or device UUID. */
  fallback?: string | null;
  /** Tooltip + accessible label. Defaults to "Copy device ID". */
  title?: string;
  size?: number;
  sx?: SxProps<Theme>;
}

/**
 * Copies a device's canonical identifier. Prefers `machine_id` — the id used in
 * MQTT, analytics, and backend device search — and falls back to whatever
 * identifier is displayed when a device has no machine_id. Renders nothing when
 * neither value is available.
 */
export function CopyDeviceId({
  machineId,
  fallback,
  title = 'Copy device ID',
  size,
  sx,
}: CopyDeviceIdProps) {
  const value = machineId?.trim() || fallback?.trim() || '';
  if (!value) return null;
  return <CopyButton text={value} title={title} size={size} sx={sx} />;
}
